import type { CanEntitlement, OrganizationEntitlementChecker } from '#layers/access/public';
import {
	canEditOrganizationSettings,
	canReadApiKeys,
	canReadAuditLogs,
	canReadAuthProviders,
	canReadOrganizationExtensions,
	canReadOrganizationLicense,
	canReadOrganizationMembers,
	canReadServiceAccounts,
} from '#layers/access/public';

const SETTINGS_SECTION_ENTITLEMENTS = {
	'account/profile': null,
	'account/access': null,
	'organization/workspaces': null,
	'organization/extensions': () => canReadOrganizationExtensions,
	'organization/members': () => canReadOrganizationMembers,
	'organization/general': () => canEditOrganizationSettings,
	'organization/api-keys': () => canReadApiKeys,
	'organization/service-accounts': () => canReadServiceAccounts,
	'organization/sso': () => canReadAuthProviders,
	'organization/audit-logs': () => canReadAuditLogs,
	'organization/license': () => canReadOrganizationLicense,
	// Gated by the `featureFlagsVisible` flag (see settings-overlay-panels), not an org entitlement.
	'developer/feature-flags': null,
} as const satisfies Record<string, (() => CanEntitlement) | null>;

export type SettingsSection = keyof typeof SETTINGS_SECTION_ENTITLEMENTS;

export const DEFAULT_SECTION: SettingsSection = 'account/profile';

/** Build the `/~/…` backdrop-workspace path that opens the settings overlay at the given section. */
export function settingsOverlayPath(section: SettingsSection): string {
	return `/~/${section}`;
}

export function isSettingsSection(section: string | null): section is SettingsSection {
	return section !== null && Object.hasOwn(SETTINGS_SECTION_ENTITLEMENTS, section);
}

export function canViewSettingsSection(section: SettingsSection, canOrganization: OrganizationEntitlementChecker): boolean {
	const entitlement = SETTINGS_SECTION_ENTITLEMENTS[section];
	return entitlement === null || canOrganization(entitlement());
}

export function resolveSettingsSection(section: string | null, canOrganization: OrganizationEntitlementChecker): SettingsSection {
	return isSettingsSection(section) && canViewSettingsSection(section, canOrganization)
		? section
		: DEFAULT_SECTION;
}
